import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Mail, Plus, Edit2, Trash2, Save, X, Eye, Send, RefreshCw,
  CheckCircle, XCircle, Code, Variable
} from 'lucide-react';
import TipTapEditor from '../../components/ui/TipTapEditor';
import LanguageTabs, { useAvailableLanguages } from '../../components/admin/LanguageTabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Available email template variables with descriptions
const EMAIL_VARIABLES = {
  userName: 'Imię użytkownika',
  firstName: 'Imię',
  lastName: 'Nazwisko',
  fullName: 'Pełne imię i nazwisko',
  email: 'Adres email użytkownika',
  userEmail: 'Adres email użytkownika',
  bookingUrl: 'Link do rezerwacji',
  bookingDate: 'Data rezerwacji',
  bookingTime: 'Godzina rezerwacji',
  className: 'Nazwa zajęć/typu wejścia',
  instructorName: 'Imię i nazwisko instruktora',
  passName: 'Nazwa karnetu',
  passCode: 'Kod karnetu (do aktywacji)',
  passValidUntil: 'Data ważności karnetu',
  remainingEntries: 'Pozostałe wejścia na karnecie',
  amount: 'Kwota płatności',
  price: 'Cena karnetu',
  resetLink: 'Link do resetowania hasła',
  activationCode: 'Kod aktywacyjny karnetu podarunkowego',
  activationUrl: 'Link do strony aktywacji karnetu',
  activationDeadline: 'Data graniczna aktywacji (sformatowana)',
  activationDeadlineDays: 'Ilość dni na aktywację karnetu (liczba)',
  daysRemaining: 'Dni pozostałe do końca terminu aktywacji',
  purchaseDate: 'Data zakupu karnetu',
  giftMessage: 'Wiadomość od kupującego (karnet podarunkowy)',
  recipientName: 'Imię odbiorcy karnetu podarunkowego',
  recipientEmail: 'Email odbiorcy karnetu podarunkowego',
  senderName: 'Imię osoby kupującej prezent',
  applicableItems: 'Na co obowiązuje rabat (karnety/sesje/usługi)'
};

const AdminEmailTemplates = () => {
  const { token } = useAdminAuth();
  const { languages, loading: languagesLoading } = useAvailableLanguages();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('pl');
  const [testEmail, setTestEmail] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState('pl');

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Nie udało się pobrać szablonów');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleInitialize = async () => {
    try {
      const response = await axios.post(
        `${API}/api/admin/email-templates/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Zainicjalizowano ${response.data.count} szablonów`);
      fetchTemplates();
    } catch (error) {
      toast.error('Nie udało się zainicjalizować szablonów');
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    try {
      if (isCreating) {
        await axios.post(
          `${API}/api/admin/email-templates`,
          editingTemplate,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Szablon utworzony');
      } else {
        await axios.put(
          `${API}/api/admin/email-templates/${editingTemplate.id}`,
          editingTemplate,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Szablon zaktualizowany');
      }
      setEditingTemplate(null);
      setIsCreating(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać szablonu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten szablon?')) return;

    try {
      await axios.delete(`${API}/api/admin/email-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Szablon usunięty');
      fetchTemplates();
    } catch (error) {
      toast.error('Nie udało się usunąć szablonu');
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(
        `${API}/api/admin/email-templates/${id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTemplates();
    } catch (error) {
      toast.error('Nie udało się zmienić statusu');
    }
  };

  const handleSendTest = async () => {
    if (!editingTemplate || !testEmail) {
      toast.error('Wprowadź adres email');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/admin/email-templates/${editingTemplate.id}/test`,
        { email: testEmail, lang: activeTab },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email testowy zostałby wysłany');
      console.log('Preview:', response.data.preview);
    } catch (error) {
      toast.error('Nie udało się wysłać emaila testowego');
    }
  };

  const startEditing = (template) => {
    setEditingTemplate({ ...template });
    setIsCreating(false);
    setShowPreview(false);
  };

  const startCreating = () => {
    setEditingTemplate({
      type: '',
      name: '',
      subject: { pl: '', en: '' },
      content: { pl: '', en: '' },
      variables: [],
      isActive: true
    });
    setIsCreating(true);
    setShowPreview(false);
  };

  const updateField = (field, value, lang = null) => {
    setEditingTemplate(prev => {
      if (lang) {
        return {
          ...prev,
          [field]: { ...prev[field], [lang]: value }
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const renderVariablesList = (variables) => {
    if (!variables || variables.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {variables.map(v => (
          <Badge key={v} variant="outline" className="text-xs font-mono">
            {'{{' + v + '}}'}
          </Badge>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-white/5 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Szablony emaili
          </h1>
          <p className="text-gray-400 mt-1">
            Zarządzaj szablonami wiadomości email wysyłanych przez system
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleInitialize}
            className="border-white/20"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Zainicjalizuj domyślne
          </Button>
          <Button onClick={startCreating}>
            <Plus className="w-4 h-4 mr-2" />
            Nowy szablon
          </Button>
        </div>
      </div>

      {/* Editor Modal */}
      {editingTemplate && (
        <Card className="bg-[#1a1a2e] border-white/10">
          <CardHeader className="border-b border-white/10">
            <div className="flex justify-between items-center">
              <CardTitle className="text-white">
                {isCreating ? 'Nowy szablon' : `Edytuj: ${typeof editingTemplate.name === 'object' ? (editingTemplate.name?.pl || editingTemplate.name?.en || editingTemplate.type) : editingTemplate.name}`}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {showPreview ? 'Edytor' : 'Podgląd'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditingTemplate(null); setIsCreating(false); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Identyfikator (type)
                </label>
                <Input
                  value={editingTemplate.type}
                  onChange={(e) => updateField('type', e.target.value)}
                  disabled={!isCreating}
                  placeholder="np. booking_confirmation"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nazwa (dla admina)
                </label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="np. Potwierdzenie rezerwacji"
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>

            {/* Variables reference - always visible */}
            <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg space-y-3">
              <div className="flex items-center gap-2 text-purple-300">
                <Code className="w-4 h-4" />
                <span className="font-medium">Dostępne zmienne do użycia w szablonie:</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                {Object.entries(EMAIL_VARIABLES).map(([variable, description]) => (
                  <div key={variable} className="flex items-center gap-2">
                    <code className="px-2 py-0.5 bg-black/30 rounded text-purple-400 text-xs font-mono">
                      {`{{${variable}}}`}
                    </code>
                    <span className="text-gray-400 text-xs">{description}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Wklej zmienną w treści lub temacie emaila - zostanie automatycznie zamieniona na odpowiednią wartość.
              </p>
            </div>

            {/* Custom variables input */}
            <div className="p-4 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                <Variable className="w-4 h-4" />
                <span className="font-medium">Dodatkowe zmienne szablonu:</span>
              </div>
              <Input
                value={editingTemplate.variables?.join(', ') || ''}
                onChange={(e) => updateField('variables', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
                placeholder="Własne zmienne (oddzielone przecinkami)"
                className="bg-white/5 border-white/10"
              />
            </div>

            {/* Language tabs */}
            <div className="space-y-4">
              <LanguageTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                showIndicator={true}
                hasContent={languages.reduce((acc, lang) => {
                  acc[lang.code] = !!(editingTemplate.subject?.[lang.code] || editingTemplate.content?.[lang.code]);
                  return acc;
                }, {})}
              />

              {languages.map(lang => (
                activeTab === lang.code && (
                  <div key={lang.code} className="space-y-4 mt-4">
                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Temat emaila ({lang.nativeName})
                      </label>
                      <Input
                        value={editingTemplate.subject?.[lang.code] || ''}
                        onChange={(e) => updateField('subject', e.target.value, lang.code)}
                        placeholder={`Temat wiadomości (${lang.code.toUpperCase()})...`}
                        className="bg-white/5 border-white/10"
                      />
                    </div>

                    {/* Content */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Treść emaila ({lang.nativeName})
                      </label>
                      {showPreview ? (
                        <div 
                          className="prose prose-invert max-w-none p-4 bg-white/5 rounded-lg min-h-[300px] border border-white/10"
                          dangerouslySetInnerHTML={{ 
                            __html: editingTemplate.content?.[lang.code] || '<p class="text-gray-500">Brak treści</p>' 
                          }}
                        />
                      ) : (
                        <TipTapEditor
                          content={editingTemplate.content?.[lang.code] || ''}
                          onChange={(value) => updateField('content', value, lang.code)}
                          placeholder={`Treść wiadomości email (${lang.code.toUpperCase()})...`}
                          variables={Object.keys(EMAIL_VARIABLES)}
                          showSourceView={true}
                        />
                      )}
                    </div>
                  </div>
                )
              ))}
            </div>

            {/* Test email */}
            {!isCreating && (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email testowy
                  </label>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <Button variant="outline" onClick={handleSendTest}>
                  <Send className="w-4 h-4 mr-2" />
                  Wyślij test
                </Button>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
              <Button 
                variant="outline" 
                onClick={() => { setEditingTemplate(null); setIsCreating(false); }}
              >
                Anuluj
              </Button>
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                {isCreating ? 'Utwórz' : 'Zapisz'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates list */}
      <div className="space-y-4">
        {templates.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                Brak szablonów
              </h3>
              <p className="text-gray-400 mb-4">
                Kliknij "Zainicjalizuj domyślne" aby dodać standardowe szablony
              </p>
              <Button onClick={handleInitialize}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Zainicjalizuj domyślne
              </Button>
            </CardContent>
          </Card>
        ) : (
          templates.map(template => (
            <Card 
              key={template.id} 
              className={`mb-4 bg-white/5 border-white/10 ${!template.isActive ? 'opacity-60' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-white">
                        {typeof template.name === 'object' ? (template.name?.pl || template.name?.en || 'Brak nazwy') : template.name}
                      </h3>
                      <Badge variant="outline" className="font-mono text-xs">
                        {template.type}
                      </Badge>
                      {template.isActive ? (
                        <Badge className="bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Aktywny
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400">
                          <XCircle className="w-3 h-3 mr-1" />
                          Nieaktywny
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mb-2">
                      <strong>Temat (PL):</strong> {typeof template.subject === 'object' ? (template.subject?.pl || '-') : template.subject}
                    </p>
                    {renderVariablesList(template.variables)}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={template.isActive}
                      onCheckedChange={() => handleToggle(template.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(template)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
